import React, { useState, useEffect, useContext, useCallback } from "react";
import { Drawer, Col, Modal } from "antd";
import API from "../../API";
import CustomList from "../components/List";
import ListItem from "../components/ListItem";
import ListItemActions from "../components/ListItemActions";
import CustomPagination from "../components/CustomPagination";
import AddDocumentForm from "./AddDocument";
import { notifyError, notifySuccess } from "../../Util";
import "./styles.css";
import { API_BASE_URL } from "../../API/config";
import { AppContext } from "../../context/AppContext";
import * as AppPermissions from "../../Util/permissions";
// removed legacy procuring entity handling

const title = { xxl: 5, xl: 5, lg: 5, md: 5, sm: 12, xs: 24 };
const description = { xxl: 5, xl: 5, lg: 5, md: 5, sm: 6, xs: 24 };
const category = { xxl: 4, xl: 4, lg: 4, md: 4, sm: 4, xs: 24 };
const type = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 0, xs: 0 };
const status = { xxl: 3, xl: 3, lg: 3, md: 3, sm: 0, xs: 0 };

const headerLayout = [
  { ...title, header: "Document Title" },
  // { ...tags, header: "Tags" },
  { ...description, header: "Description" },
  { ...category, header: "Document Category" },
  { ...type, header: "Document Type" },
  { ...status, header: "Status" },
];

const { confirm } = Modal;

const Document = ({ match }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editableDocument, setEditableDocument] = useState(null);
  const [documentsList, setDocumentsList] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0,
    has_more_pages: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formErrors, setFormErrors] = useState({});

  const { permissions, selectedProject, selectedProcuringEntity } = useContext(AppContext);
  const ensuredProcuringEntityId = match?.params?.procuringEntityId || null;

  const hasPermissions = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    const userPermissions = permissions;
    const status = requiredPermissions?.every((permission) =>
      userPermissions?.includes(permission)
    );
    return status;
  };

  const effectiveProcuringEntityId = ensuredProcuringEntityId || selectedProcuringEntity;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProject, effectiveProcuringEntityId]);

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const showDrawer = () => {
    setVisible(true);
    setIsEdit(false); 
    setEditableDocument(null);
  };

  const onClose = () => {
    setVisible(false);
    setIsEdit(false);
    setEditableDocument(null);
    setFormErrors({});
  };

  const editDocumentHandler = (docToEdit) => {
    docToEdit.category = docToEdit?.document_category?.id;
    docToEdit.type = docToEdit?.document_type?.id;
    docToEdit.project_id = docToEdit?.project_id;
    docToEdit.procuring_entity = docToEdit?.council_id;
    docToEdit.package = docToEdit?.package_id;
    docToEdit.sub_project = docToEdit?.sub_project_id;
    setEditableDocument(docToEdit);
    setIsEdit(true);
    setVisible(true);
  };

  const deleteDocument = (document) => {
    console.log({ "delete docs ====>": document });
    API.deleteDocument(document.id)
      .then((res) => {
        notifySuccess(`Document ${document.title} deleted successfully`);
        getAllDocuments();
      })
      .catch((err) => notifyError(`Error deleting document ${document.title}`));
  };

  const getAllDocuments = useCallback(() => {
    if (!selectedProject || !effectiveProcuringEntityId) return;
    
    setIsLoading(true);
    API.getDocuments({ 
      selectedProject, 
      selectedProcuringEntity: effectiveProcuringEntityId,
      page: currentPage,
      per_page: pageSize
    }).then((res) => {
      const { data, pagination: paginationData } = res;
      setDocumentsList(data);
      setPagination(paginationData || {
        current_page: 1,
        per_page: pageSize,
        total: data.length,
        last_page: 1,
        from: 1,
        to: data.length,
        has_more_pages: false
      });
      setIsLoading(false);
    }).catch((err) => {
      console.error("Failed to fetch documents", err);
      setIsLoading(false);
    });
  }, [selectedProject, effectiveProcuringEntityId, currentPage, pageSize]);
  const showArchiveConfirm = (item) => {
    confirm({
      title: `Are you sure you want to archive document ${item.title}?`,
      okButtonProps: {
        "data-testid": "archive-confirm-role-ok-button",
      },
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteDocument(item);
      },
    });
  };

  const handleSubmit = (values, form) => {
    const documentFormData = new FormData();
    const appendIfDefined = (key, value) => {
      if (value !== undefined && value !== null) {
        documentFormData.append(key, value);
      }
    };

    appendIfDefined("title", values.title);
    appendIfDefined("description", values.description);
    appendIfDefined("project_id", values.project_id);
    appendIfDefined("council_id", values.procuring_entity);
    appendIfDefined("package_id", values.package);
    appendIfDefined("sub_project_id", values.sub_project);
    appendIfDefined("status", values.status);
    appendIfDefined("document_type_id", values.type);
    appendIfDefined("document_category_id", values.category);

    if (values.upload && values.upload[0]) {
      documentFormData.append("file", values.upload[0].originFileObj);
    }

    setIsLoading(true);
    if (!isEdit) {
      API.createDocument(documentFormData)
        .then((res) => {
          setIsLoading(false);
          notifySuccess("Document added successfully");
          setVisible(false);
          getAllDocuments();
          form.resetFields();
        })
        .catch((err) => {
          setIsLoading(false);
          if (err.response && err.response.data && err.response.data.errors) {
            const errors = err.response.data.errors;
            setFormErrors(errors);
            Object.keys(errors).forEach(fieldName => {
              const fieldErrors = errors[fieldName];
              if (fieldName === 'file') {
                notifyError(fieldErrors[0]);
              } else {
                form.setFields([
                  {
                    name: fieldName,
                    errors: fieldErrors,
                  },
                ]);
              }
            });
          } else {
            notifyError("Failed to add document");
          }
        });
      return;
    }

    API.editDocument(documentFormData, values.id)
      .then((res) => {
        setIsLoading(false);
        notifySuccess("Document updated successfully");
        setVisible(false);
        getAllDocuments();
        form.resetFields();
      })
      .catch((err) => {
        setIsLoading(false);
        if (err.response && err.response.data && err.response.data.errors) {
          const errors = err.response.data.errors;
          setFormErrors(errors);
          Object.keys(errors).forEach(fieldName => {
            const fieldErrors = errors[fieldName];
            if (fieldName === 'file') {
              notifyError(fieldErrors[0]);
            } else {
              form.setFields([
                {
                  name: fieldName,
                  errors: fieldErrors,
                },
              ]);
            }
          });
        } else {
          notifyError("Failed to update document");
        }
      });
  };

  useEffect(() => {
    getAllDocuments();
  }, [getAllDocuments]);

  useEffect(() => {
    console.log("UPDATED Documents LISTS Array:", documentsList);
  }, [documentsList]);

  return (
    <>
  {!effectiveProcuringEntityId && (
        <div style={{ padding: 24 }}>
          Please select a council (LGA) for this project to view documents.
        </div>
      )}
  <div className="document-container" style={!effectiveProcuringEntityId ? { opacity: 0.4, pointerEvents: 'none' } : { padding: 16 }}>
        <CustomList
          itemName="Document"
          datatestid="Documents-list"
          title={"Documents List"}
          actionButtonProp={{
            title: "Documents List",
            arrActions: [
              {
                datatestid: "add-role-button",
                btnName: "Add New Document",
                btnAction: showDrawer,
              },
            ],
          }}
          items={documentsList}
          page={currentPage}
          itemCount={pagination.total || 0}
          loading={isLoading}
          // onRefresh={() => getAllDocuments()}
          headerLayout={headerLayout}
          renderListItem={({ item }) => (
            <ListItem
              key={item.id} 
              name={item?.title}
              item={item}
              renderActions={() => (
                <ListItemActions
                  edit={{
                    name: "Edit Document",
                    datatestid: `edit-Document-${item.id}`,
                    title: "Edit Document",
                    onClick: () => editDocumentHandler(item),
                  }}
                  archive={{
                    name: "Archive Document",
                    datatestid: `archive-Document-${item.id}`,
                    title: "Archive Document",
                    onClick: () => showArchiveConfirm(item),
                  }}
                  downloadReport={
                    hasPermissions([AppPermissions.CAN_DOWNLOAD_DOCUMENT])
                      ? {
                          name: "Download Document",
                          title: "Click to download the Document",
                          url: `${API_BASE_URL}/storage/${item?.document_url}`,
                        }
                      : undefined
                  }
                />
              )}
            >
              {/* eslint-disable react/jsx-props-no-spreading */}

              <Col
                {...title}
                className="contentEllipse"
                title={item?.title || "-"}
                data-label="Document Title"
              >
                {item?.title || "-"}
              </Col>

              <Col
                {...description}
                className="contentEllipse"
                title={item?.description || "-"}
                data-label="Description"
              >
                {item?.description || "-"}
              </Col>

              <Col
                {...category}
                className="contentEllipse"
                title={"Document Category/Section"}
                data-label="Category"
              >
                {item?.document_category?.name || "-"}
              </Col>

              <Col 
                {...type} 
                className="contentEllipse" 
                title={"Document Type"}
                data-label="Type"
              >
                {item?.document_type?.name || "-"}
              </Col>

              <Col 
                {...status} 
                className="contentEllipse" 
                title={"Status"}
                data-label="Status"
              >
                {item?.status || ""}
              </Col>

              {/* eslint-enable react/jsx-props-no-spreading */}
            </ListItem>
          )}
        />
        
        {pagination && pagination.total > 0 && (
          <CustomPagination 
            pagination={pagination}
            onPageChange={handlePageChange}
            loading={isLoading}
          />
        )}
        
        <Drawer
          title={isEdit ? "Edit Document" : "Add New Document"}
          placement="right"
          onClose={onClose}
          visible={visible}
          width={650}
        >
          <AddDocumentForm
            setDocumentsList={setDocumentsList}
            setVisible={setVisible}
            onSubmit={handleSubmit}
            initialValues={isEdit ? editableDocument : null}
            key={editableDocument ? editableDocument.title : "new-document"}
            packageId={effectiveProcuringEntityId}
            formErrors={formErrors}
          />
        </Drawer>
      </div>
    </>
  );
};

export default Document;
